import { useState } from "react";
import { ArrowLeft, Check, Mail, PencilLine } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import "@/pages/StudioPartner.css";
import TurnstileWidget from "@/components/TurnstileWidget";

const EMPTY_FORM = {
  name: "",
  studio: "",
  email: "",
  phone: "",
  instagram: "",
  message: "",
  consent: false,
  website: "",
  turnstile_token: "",
};

export default function StudioPartner() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const submit = async (event) => {
    event.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Bitte Name und E-Mail ausfüllen.");
      return;
    }
    if (!form.consent) {
      toast.error("Bitte dem Datenschutz zustimmen.");
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/public/studio-interest", form);
      setSent(true);
      setForm(EMPTY_FORM);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Anfrage konnte nicht gesendet werden.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="partner-page" style={{ "--partner-background": "url(/dystopia-background-web.webp)" }}>
      <div className="partner-page-inner">
        <header className="partner-topbar">
          <a href="/" className="partner-brand" aria-label="Zurück zu TattooGen">
            <span className="brand-wordmark">ARTPART</span>
            <span className="brand-subline">TattooGen</span>
          </a>
          <a href="/" className="partner-back"><ArrowLeft size={16} /> Zu TattooGen</a>
        </header>

        {sent ? (
          <section className="partner-success" aria-live="polite">
            <span className="partner-success-icon"><Check size={28} /></span>
            <div className="partner-kicker">Anfrage angekommen</div>
            <h1>Danke für dein Interesse.</h1>
            <p>Ich schaue mir dein Studio an und melde mich persönlich bei dir. Alles Weitere besprechen wir direkt und ohne Verkaufsautomat.</p>
            <a href="/" className="partner-primary">Zurück zu TattooGen</a>
          </section>
        ) : (
          <>
            <section className="partner-hero">
              <div className="partner-kicker">TattooGen für dein Studio</div>
              <h1>Ein Ideengeber, der deinen eigenen Stil nicht ersetzt.</h1>
              <p className="partner-lead">TattooGen gibt Kundinnen und Kunden einen einfachen Einstieg in die Motivfindung. Die Auswahl bleibt Inspiration – du entwickelst daraus die individuelle Handzeichnung für Person, Körperstelle und Größe.</p>
            </section>

            <section className="partner-content-grid">
              <article className="partner-story">
                <div className="partner-story-mark">RG</div>
                <div className="partner-kicker">Warum ich das gebaut habe</div>
                <h2>Hi, ich bin René.</h2>
                <p>Ich tätowiere seit rund zwölf Jahren und arbeite in meinem eigenen ruhigen Studio. TattooGen habe ich zuerst als kleines Werkzeug für mich selbst entwickelt: als weiteren Weg, über den Menschen meine Arbeit entdecken und mit einer eigenen Idee bei mir anfragen können – unabhängiger von Instagram.</p>
                <p>Die Mischung ist dabei nie das fertige Tattoo. Sie startet das Gespräch. Danach zeichne ich eine neue, zusammenhängende Komposition von Hand und setze sie in meinem Stil um.</p>

                <div className="partner-benefits">
                  <div><PencilLine size={20} /><span><strong>Deine Handschrift bleibt zentral.</strong> Das Ergebnis wird von dir gezeichnet, nicht aus der Auswahl kopiert.</span></div>
                  <div><Mail size={20} /><span><strong>Ein neuer Einstieg für Kunden.</strong> Weniger leere Anfragen, mehr konkrete Richtungen für das erste Gespräch.</span></div>
                  <div><Check size={20} /><span><strong>Für dein Studio angepasst.</strong> Gestaltung, Motive und Ablauf können zu deinem Auftritt und deiner Arbeitsweise passen.</span></div>
                </div>

                <p className="partner-note">Wenn dich das interessiert, schick mir kurz deine Kontaktdaten. Ich melde mich persönlich und erkläre dir die Details.</p>
              </article>

              <section className="partner-form-card">
                <div className="partner-kicker">Unverbindlich anfragen</div>
                <h2>Erzähl mir kurz, wer du bist.</h2>
                <p>Das dauert ungefähr eine Minute.</p>

                <form onSubmit={submit} className="partner-form">
                  <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={(event) => set("website", event.target.value)}
                    className="partner-honeypot"
                    aria-hidden="true"
                  />

                  <div className="partner-field">
                    <Label htmlFor="partner-name">Name *</Label>
                    <Input id="partner-name" value={form.name} onChange={(event) => set("name", event.target.value)} autoComplete="name" />
                  </div>
                  <div className="partner-field">
                    <Label htmlFor="partner-studio">Studio <span>(optional)</span></Label>
                    <Input id="partner-studio" value={form.studio} onChange={(event) => set("studio", event.target.value)} autoComplete="organization" />
                  </div>
                  <div className="partner-field">
                    <Label htmlFor="partner-email">E-Mail *</Label>
                    <Input id="partner-email" type="email" value={form.email} onChange={(event) => set("email", event.target.value)} autoComplete="email" />
                  </div>
                  <div className="partner-field-grid">
                    <div className="partner-field">
                      <Label htmlFor="partner-phone">Telefon <span>(optional)</span></Label>
                      <Input id="partner-phone" type="tel" value={form.phone} onChange={(event) => set("phone", event.target.value)} autoComplete="tel" />
                    </div>
                    <div className="partner-field">
                      <Label htmlFor="partner-instagram">Instagram <span>(optional)</span></Label>
                      <Input id="partner-instagram" value={form.instagram} onChange={(event) => set("instagram", event.target.value)} placeholder="@deinstudio" />
                    </div>
                  </div>
                  <div className="partner-field">
                    <Label htmlFor="partner-message">Nachricht <span>(optional)</span></Label>
                    <Textarea id="partner-message" rows={3} value={form.message} onChange={(event) => set("message", event.target.value)} placeholder="Was interessiert dich an TattooGen?" />
                  </div>

                  <label className="partner-consent">
                    <Checkbox checked={form.consent} onCheckedChange={(value) => set("consent", !!value)} />
                    <span>Ich stimme zu, dass meine Angaben zur Bearbeitung meiner Anfrage gespeichert und zur Kontaktaufnahme verwendet werden. *</span>
                  </label>

                  <TurnstileWidget onToken={(token) => set("turnstile_token", token)} />

                  <button type="submit" className="partner-submit" disabled={submitting}>
                    {submitting ? "Wird gesendet…" : "Interesse senden"} <span>→</span>
                  </button>
                  <small className="partner-form-footnote">Keine automatische Anmeldung und kein Newsletter. René meldet sich persönlich.</small>
                </form>
              </section>
            </section>
          </>
        )}
      </div>
    </main>
  );
}
